import { useNavigate, useLocation } from 'react-router-dom';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTransition, animated, useSpringRef } from '@react-spring/web';
import { useSelector } from 'react-redux';
import queryString from 'query-string';
import orderApi from '../../services/orderApi';
import {
  TrashIcon,
  PlayCircleIcon,
  MagnifyingGlassIcon,
  DocumentChartBarIcon,
  ExclamationTriangleIcon,
  FunnelIcon,
} from '@heroicons/react/24/outline';
import companyApi from '../../services/companyApi';
import storeApi from '../../services/storeApi';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { UserRoles } from '../../data/enums';
import NoOrders from '../../assets/no-orders.svg';
import { PaymentStatus, OrderStatus, PlatformTypes } from '../../data/enums';
import { setStartAndEndDate } from '../../utils/setStartAndEndDate';
import OrdersSkeleton from './components/orders-skeleton';
import {
  TWButton,
  TWInput,
  TWSelect,
  TWDateRangePicker,
  TWDataTable,
  TWModal,
  EmptyState,
} from '../../components/ui';
import { buildOrderColumns } from './columns';
import { GOOGLE_ADS_SOURCE } from '../integrations/google-ads/google-ads-settings';
import './orders.css';

dayjs.extend(customParseFormat);
const utc = require('dayjs/plugin/utc');
const timezone = require('dayjs/plugin/timezone');

dayjs.extend(utc);
dayjs.extend(timezone);

const Orders = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const searchParams = queryString.parse(location.search);

  const cancelButtonRef = useRef(null);
  const globalCompany = useSelector((state) => state.companyReducer.selected);
  const userRoles = useSelector((state) => state.authReducer.roles);
  const isAdmin = userRoles.includes(UserRoles.ADMIN);
  const [orderList, setOrderList] = useState([]);
  const [companyList, setCompanyList] = useState([]);
  const [storeList, setStoreList] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  const [refundApprovalCount, setRefundApprovalCount] = useState(0);
  const [selectedStore, setSelectedStore] = useState(null);
  const [errors, setErrors] = useState({ store: '' });

  const [search, setSearch] = useState({
    search: searchParams.search || '',
    companyid: searchParams.companyid || '',
    startdate: searchParams.startdate || '',
    enddate: searchParams.enddate || '',
    storeid: searchParams.storeid || '',
    orderstatusses: searchParams.orderstatusses || [],
    paymentstatus: searchParams.paymentstatus || '20',
    marketingsource: searchParams.marketingsource || '',
    PageSize: searchParams.PageSize || 20,
    PageNumber: searchParams.PageNumber || 1,
  });

  const [dialogs, setDialogs] = useState({ liveOrdersDialogs: false });

  const [loading, setLoading] = useState({
    listLoading: true,
    upsertLoading: false,
    deleteLoading: false,
  });
  const [pagination, setPagination] = useState({
    pageNumber: 1,
    pageSize: 20,
    totalRecords: null,
  });

  const transRef = useSpringRef();
  const transitions = useTransition(showFilters, {
    ref: transRef,
    keys: null,
    from: { maxHeight: '0px', opacity: 0 },
    enter: { maxHeight: '1000px', opacity: 1, overflowY: 'none' },
    leave: { maxHeight: '0px', opacity: 0 },
  });

  const getOrderList = useCallback(async () => {
    setLoading((prev) => ({ ...prev, listLoading: true }));
    setOrderList([]);

    navigate({
      pathname: location.pathname,
      search: queryString.stringify(search, { skipNull: true }),
    });

    try {
      const companyId = globalCompany?.id;
      if (!isAdmin && !companyId) {
        setOrderList([]);
        setLoading((prev) => ({ ...prev, listLoading: false }));
        return;
      }

      const query = {
        PageNumber: pagination.pageNumber,
        PageSize: pagination.pageSize,
        deleted: false,
        startdate:
          search.startdate && search.startdate !== ''
            ? setStartAndEndDate(search.startdate, 'startdate', '0')
            : '',
        enddate:
          search.enddate && search.enddate !== ''
            ? setStartAndEndDate(search.enddate, 'enddate', '0')
            : '',
        ...search,
        companyid: isAdmin ? '' : companyId,
      };

      const list = await orderApi.list(query);
      if (list) {
        setOrderList(list.data.data);
        setPagination({
          pageNumber: list.data.pageNumber,
          pageSize: list.data.pageSize,
          totalRecords: list.data.totalRecords,
        });
      }
    } catch (error) {}
    setLoading((prev) => ({ ...prev, listLoading: false }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, location.pathname, navigate, globalCompany?.id]);

  const getRefundApprovalCount = useCallback(async () => {
    try {
      const companyId = globalCompany?.id;
      if (!isAdmin && !companyId) {
        setRefundApprovalCount(0);
        return;
      }
      const query = {
        PageNumber: 1,
        PageSize: 1,
        deleted: false,
        hasrefundapproval: true,
        companyid: isAdmin ? '' : companyId,
      };
      const list = await orderApi.list(query);
      setRefundApprovalCount(list?.data?.totalRecords || 0);
    } catch (error) {
      setRefundApprovalCount(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [globalCompany?.id, userRoles]);

  useEffect(() => {
    getOrderList();
  }, [getOrderList]);

  const getCompanyList = async () => {
    try {
      const query = `?pageSize=1000`;
      const list = await companyApi.list(query);
      if (list.data.data.length > 0) setCompanyList(list.data.data);
    } catch (error) {}
  };

  const getStoreList = async () => {
    const companyId = globalCompany?.id;
    if (!isAdmin && !companyId) {
      setStoreList([]);
      return;
    }
    const query = isAdmin ? '' : `&companyid=${companyId}`;
    try {
      const list = await storeApi.list(query);
      if (list.data.data.length > 0) setStoreList(list.data.data);
    } catch (error) {}
  };

  const onClickSearch = async () => {
    await getOrderList();
    setPagination((prev) => ({ ...prev, pageNumber: 1 }));
  };

  const onFormChange = (value, key) =>
    setSearch((prev) => ({ ...prev, [key]: value }));

  const onChange = (dateString) => {
    if (!dateString) {
      setSearch((prev) => ({ ...prev, startdate: '', enddate: '' }));
      return;
    }
    setSearch((prev) => ({
      ...prev,
      startdate: dateString[0],
      enddate: dateString[1],
    }));
  };

  const onClickClearSearch = () => {
    setSearch({
      search: '',
      companyid: '',
      startdate: '',
      enddate: '',
      storeid: '',
      orderstatusses: [],
      paymentstatus: '',
      marketingsource: '',
    });
    getOrderList();
  };

  const onCloseUpsert = () =>
    setDialogs((prev) => ({ ...prev, liveOrdersDialogs: false }));

  const onClickOpenLiveOrdersModal = () =>
    setDialogs((prev) => ({ ...prev, liveOrdersDialogs: true }));

  const onSubmitLiveOrdersModal = async () => {
    if (!selectedStore) {
      setErrors((prev) => ({ ...prev, store: !selectedStore }));
      return;
    }
    setDialogs((prev) => ({ ...prev, liveOrdersDialogs: false }));
    navigate(`${selectedStore}/live`);
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    transRef.start();
  }, [showFilters]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    getCompanyList();
    getStoreList();
  }, [globalCompany?.id]);
  useEffect(() => {
    getRefundApprovalCount();
  }, [getRefundApprovalCount]);

  return (
    <div className="orders-shell min-h-[calc(100vh-4rem)] bg-white px-4 py-6 dark:bg-slate-900/40 sm:px-6 lg:px-8 lg:py-8">
      <div className="mx-auto max-w-7xl space-y-8">
        <header className="reveal flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <div className="flex flex-col gap-1">
            <h1 className="text-2xl font-bold tracking-tight text-slate-900 dark:text-slate-100 sm:text-3xl">
              Orders
            </h1>
            <p className="max-w-2xl text-sm text-slate-500 dark:text-slate-400">
              View and manage all your orders in one place.
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            {isAdmin && (
              <div className="relative">
                <TWButton
                  icon={ExclamationTriangleIcon}
                  label="Refund Approvals"
                  onClick={() => navigate(`/dashboard/orders/refund-approvals`)}
                />
                {refundApprovalCount > 0 && (
                  <span className="absolute -right-1 -top-1 flex h-5 min-w-[1.25rem] items-center justify-center rounded-full bg-rose-600 px-1 text-[11px] font-bold leading-none text-white">
                    {refundApprovalCount > 99 ? '99+' : refundApprovalCount}
                  </span>
                )}
              </div>
            )}
            {isAdmin && (
              <TWButton
                icon={DocumentChartBarIcon}
                label="Summary"
                onClick={() => navigate(`/dashboard/orders/summary`)}
              />
            )}
            <TWButton
              icon={PlayCircleIcon}
              label="Live Orders"
              onClick={onClickOpenLiveOrdersModal}
            />
            <TWButton
              icon={FunnelIcon}
              label="Filters"
              onClick={() => setShowFilters(!showFilters)}
              className={
                showFilters
                  ? '!border-blue-500 !text-blue-600 dark:!border-sky-400 dark:!text-sky-400'
                  : ''
              }
            />
          </div>
        </header>

        {transitions(
          (style, item) =>
            item && (
              <animated.div style={{ ...style, overflow: 'hidden' }}>
                <section className="reveal" aria-label="Filters">
                  <div className="grid grid-cols-6 gap-4">
                    <div className="col-span-6 sm:col-span-2">
                      <TWInput
                        label="Search"
                        value={search.search}
                        onChange={(e) => onFormChange(e.target.value, 'search')}
                        placeholder="Name, phone, order #…"
                      />
                    </div>

                    {isAdmin && (
                      <div className="col-span-3 sm:col-span-2">
                        <TWSelect
                          label="Company"
                          placeholder="Company"
                          value={search.companyid}
                          onChange={(v) =>
                            setSearch((prev) => ({ ...prev, companyid: v }))
                          }
                          options={[
                            { value: '', label: 'All Company' },
                            ...companyList.map((item) => ({
                              value: item.id,
                              label: item.name,
                            })),
                          ]}
                        />
                      </div>
                    )}

                    <div className="col-span-3 sm:col-span-2">
                      <TWSelect
                        label="Store"
                        placeholder="Store"
                        value={search.storeid}
                        onChange={(v) =>
                          setSearch((prev) => ({ ...prev, storeid: v }))
                        }
                        options={[
                          { value: '', label: 'All Stores' },
                          ...(search.companyid
                            ? storeList.filter(
                                (store) => store.companyid === search.companyid,
                              )
                            : storeList
                          ).map((item) => ({
                            value: item.id,
                            label: item.name,
                          })),
                        ]}
                      />
                    </div>

                    <div className="col-span-3 sm:col-span-2">
                      <TWSelect
                        label="Order Status"
                        placeholder="Order Status"
                        value={search.orderstatusses}
                        onChange={(v) =>
                          setSearch((prev) => ({ ...prev, orderstatusses: v }))
                        }
                        options={Object.keys(OrderStatus)
                          .filter((x) => x !== '40')
                          .map((key) => ({
                            value: key,
                            label: OrderStatus[key],
                          }))}
                      />
                    </div>

                    {isAdmin && (
                      <div className="col-span-3 sm:col-span-2">
                        <TWSelect
                          label="Payment Status"
                          placeholder="Payment Status"
                          value={search.paymentstatus}
                          onChange={(v) =>
                            setSearch((prev) => ({ ...prev, paymentstatus: v }))
                          }
                          options={Object.keys(PaymentStatus).map((key) => ({
                            value: key,
                            label: PaymentStatus[key],
                          }))}
                        />
                      </div>
                    )}

                    <div className="col-span-6 sm:col-span-2">
                      <TWDateRangePicker
                        label="Date Range"
                        value={[
                          search.startdate
                            ? dayjs(
                                search.startdate,
                                'YYYY-MM-DDT00:00:00:000Z',
                              )
                            : null,
                          search.enddate
                            ? dayjs(search.enddate, 'YYYY-MM-DDT23:59:59:999Z')
                            : null,
                        ]}
                        onChange={onChange}
                      />
                    </div>

                    <div className="col-span-3 sm:col-span-2">
                      <TWSelect
                        label="Platforms"
                        placeholder="Platforms"
                        value={search.platform}
                        onChange={(v) =>
                          setSearch((prev) => ({ ...prev, platform: v }))
                        }
                        options={Object.keys(PlatformTypes).map((key) => ({
                          value: key,
                          label: PlatformTypes[key],
                        }))}
                      />
                    </div>

                    <div className="col-span-3 sm:col-span-2">
                      <TWSelect
                        label="Source"
                        placeholder="All sources"
                        value={search.marketingsource}
                        onChange={(v) =>
                          setSearch((prev) => ({
                            ...prev,
                            marketingsource: v,
                          }))
                        }
                        options={[
                          { value: '', label: 'All sources' },
                          { value: GOOGLE_ADS_SOURCE, label: 'Google Ads' },
                        ]}
                      />
                    </div>
                  </div>

                  <div className="mt-4 flex items-center justify-end gap-3 pb-2">
                    <TWButton
                      icon={TrashIcon}
                      label="Clear"
                      onClick={onClickClearSearch}
                    />
                    <TWButton
                      variant="primary"
                      icon={MagnifyingGlassIcon}
                      label="Search"
                      onClick={onClickSearch}
                    />
                  </div>
                </section>
              </animated.div>
            ),
        )}

        {loading.listLoading && orderList.length === 0 ? (
          <OrdersSkeleton showCompanyColumn={isAdmin} />
        ) : orderList.length > 0 ? (
          <section className="reveal">
            <TWDataTable
              columns={buildOrderColumns({ isAdmin })}
              data={orderList}
              loading={loading.listLoading}
              rowKey="id"
              rowClickPath={(order) => `${order.id}`}
              pagination={{
                showSizeChanger: true,
                current: pagination.pageNumber,
                pageSize: pagination.pageSize,
                total: pagination.totalRecords,
                showTotal: (total, range) =>
                  `Showing ${range[0]} to ${range[1]} of ${total} results`,
                onChange: (page, pageSize) =>
                  setSearch((prev) => ({
                    ...prev,
                    PageNumber: page,
                    PageSize: pageSize,
                  })),
              }}
            />
          </section>
        ) : (
          <EmptyState image={NoOrders} title="No orders found" />
        )}
      </div>

      <TWModal
        open={dialogs.liveOrdersDialogs}
        onClose={onCloseUpsert}
        title="Select a Store"
        size="lg"
        initialFocus={cancelButtonRef}
        showCloseButton
        dismissible={false}
        footer={
          <div className="flex justify-end gap-3">
            <TWButton
              ref={cancelButtonRef}
              size="lg"
              label="Close"
              onClick={onCloseUpsert}
              disabled={loading.upsertLoading}
            />
            <TWButton
              variant="primary"
              size="lg"
              label="Next"
              onClick={onSubmitLiveOrdersModal}
            />
          </div>
        }
      >
        <TWSelect
          label="Store"
          placeholder="Please select"
          error={errors.store ? 'Store can not be empty' : undefined}
          onChange={(val) => setSelectedStore(val)}
          options={storeList.map((x) => ({
            label: `${x.name} (${x.address})`,
            value: x.id,
          }))}
        />
      </TWModal>
    </div>
  );
};

export default Orders;
